import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SEO COMPONENT - RC Studios
 * Handles meta tags, Open Graph, Twitter Cards, and JSON-LD structured data
 * ═══════════════════════════════════════════════════════════════════════════
 */

const SITE_NAME = 'RC Studios';
const SITE_URL = 'https://rcstudios.net';
const DEFAULT_IMAGE = `${SITE_URL}/og-default-v3.jpg?v=3`;
const TWITTER_HANDLE = '@rcstudios';


// Business info for structured data
const BUSINESS_INFO = {
  name: 'RC Studios',
  legalName: 'RCSTUDIOS LLC',
  description: 'Premium photography, video production, and AI-powered digital media services serving Arizona and nationwide.',
  url: SITE_URL,
  email: 'marketing@rcstudios.net',
  telephone: '',
  address: {
    streetAddress: '',
    addressLocality: 'Kingman',
    addressRegion: 'AZ',
    postalCode: '',
    addressCountry: 'US'
  },
  geo: {
    latitude: 35.1894,
    longitude: -114.0530
  },
  areaServed: [
    'Kingman, Arizona',
    'Lake Havasu City, Arizona',
    'Bullhead City, Arizona',
    'Mohave County, Arizona',
    'Arizona',
    'United States'
  ],
  priceRange: '$$-$$$',
  openingHours: 'Mo-Fr 09:00-18:00',
  image: DEFAULT_IMAGE,
  sameAs: [
    'https://www.instagram.com/overlandishadventures/',
    'https://www.facebook.com/rcstudios.media',
    'https://youtube.com/@rcstudios'
  ]
};

/**
 * SEO Component
 * @param {Object} props
 * @param {string} props.title - Page title (will append site name)
 * @param {string} props.description - Meta description (155-160 chars ideal)
 * @param {string} props.path - Page path for canonical URL
 * @param {string} props.image - OG image URL (optional - will auto-generate if not provided)
 * @param {string} props.type - OG type (website, article, product)
 * @param {string} props.pageType - Page type for OG image (Service, Pricing, etc.)
 * @param {Object} props.jsonLd - JSON-LD structured data object(s)
 * @param {boolean} props.noIndex - Add noindex if true
 * @param {boolean} props.useDynamicOG - Use dynamic OG image generator (default: true for service pages)
 */
const SEO = ({
  title,
  description,
  path = '',
  image,
  type = 'website',
  pageType,
  jsonLd = null,
  noIndex = false,
  useDynamicOG = true,
  children
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const canonicalUrl = `${SITE_URL}${path}`;
  
  // Use provided image, or generate dynamic OG image, or fall back to default
  let imageUrl;
  if (image) {
    imageUrl = image.startsWith('http') ? image : `${SITE_URL}${image}`;
  } else {
    imageUrl = DEFAULT_IMAGE;
  }

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      {imageUrl === DEFAULT_IMAGE && <meta property="og:image:width" content="1200" />}
      {imageUrl === DEFAULT_IMAGE && <meta property="og:image:height" content="630" />}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:site" content={TWITTER_HANDLE} />

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
      
      {children}
    </Helmet>
  );
};

/**
 * Pre-built JSON-LD Schema Generators
 */

// Organization Schema
export const getOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: BUSINESS_INFO.name,
  legalName: BUSINESS_INFO.legalName,
  url: BUSINESS_INFO.url,
  logo: `${SITE_URL}/logo.png`,
  description: BUSINESS_INFO.description,
  email: BUSINESS_INFO.email,
  address: {
    '@type': 'PostalAddress',
    ...BUSINESS_INFO.address
  },
  sameAs: BUSINESS_INFO.sameAs
});

// LocalBusiness Schema (for Arizona services)
export const getLocalBusinessSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${SITE_URL}/#localbusiness`,
  name: BUSINESS_INFO.name,
  description: 'Professional real estate photography, video tours, drone media, and portrait photography serving Kingman, Lake Havasu City, Bullhead City, and Mohave County, Arizona.',
  url: BUSINESS_INFO.url,
  email: BUSINESS_INFO.email,
  image: BUSINESS_INFO.image,
  priceRange: BUSINESS_INFO.priceRange,
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Kingman',
    addressRegion: 'Arizona',
    addressCountry: 'US'
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: BUSINESS_INFO.geo.latitude,
    longitude: BUSINESS_INFO.geo.longitude
  },
  areaServed: [
    {
      '@type': 'City',
      name: 'Kingman',
      containedIn: 'Arizona'
    },
    {
      '@type': 'City',
      name: 'Lake Havasu City',
      containedIn: 'Arizona'
    },
    {
      '@type': 'City',
      name: 'Bullhead City',
      containedIn: 'Arizona'
    },
    {
      '@type': 'AdministrativeArea',
      name: 'Mohave County',
      containedIn: 'Arizona'
    }
  ],
  openingHoursSpecification: {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    opens: '09:00',
    closes: '18:00'
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Photography & Video Services',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Real Estate Photography'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Drone & Aerial Media'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Video Production'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Portrait Photography'
        }
      }
    ]
  }
});

// Service Schema Generator
export const getServiceSchema = ({
  name,
  description,
  url,
  image,
  provider = BUSINESS_INFO.name,
  areaServed = 'Arizona',
  priceRange = null
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name,
  description,
  url,
  image,
  provider: {
    '@type': 'LocalBusiness',
    name: provider,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Kingman',
      addressRegion: 'Arizona',
      addressCountry: 'US'
    }
  },
  areaServed: {
    '@type': areaServed === 'United States' ? 'Country' : 'State',
    name: areaServed
  },
  ...(priceRange && { priceRange })
});

// Product/Offer Schema for pricing pages
export const getProductSchema = ({
  name,
  description,
  image,
  price,
  priceCurrency = 'USD',
  url
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name,
  description,
  image,
  url,
  offers: {
    '@type': 'Offer',
    price,
    priceCurrency,
    availability: 'https://schema.org/InStock',
    seller: {
      '@type': 'Organization',
      name: BUSINESS_INFO.name
    }
  }
});

// Breadcrumb Schema Generator
export const getBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `${SITE_URL}${item.path}`
  }))
});

// FAQ Schema Generator
export const getFAQSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(faq => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer
    }
  }))
});

export default SEO;
export { SITE_URL, SITE_NAME, BUSINESS_INFO };
